/* eslint-disable max-lines */
import {use3D} from '@gaia/hooks/use3D';
import {useAreas} from '@gaia/hooks/useAreas';
import {useDispatchImportEffect} from '@gaia/hooks/useDispatchImportEffect';
import {useEvent} from '@gaia/hooks/useEvent';
import useFitMapToFolderEffect from '@gaia/hooks/useFitMapToFolderEffect';
import {useFitMapToImportEffect} from '@gaia/hooks/useFitMapToImportEffect';
import useFitMapToSelectionEffect from '@gaia/hooks/useFitMapToSelectionEffect';
import {useFolders} from '@gaia/hooks/useFolders';
import useHighlightActiveFolder from '@gaia/hooks/useHighlightActiveFolder';
import {useImportAnalyticsEffect} from '@gaia/hooks/useImportAnalyticsEffect';
import {useImportHasSaved} from '@gaia/hooks/useImportHasSaved';
import useLaunchDarkly from '@gaia/hooks/useLaunchDarkly';
import {useLayers} from '@gaia/hooks/useLayers';
import useOverlaysState from '@gaia/hooks/useOverlaysState';
import useParentFolderVisibility from '@gaia/hooks/useParentFolderVisibility';
import usePersistentState from '@gaia/hooks/usePersistentState';
import {useReadFileEffect} from '@gaia/hooks/useReadFileEffect';
import useTakeLocalURLPropsSelectionsEffect from '@gaia/hooks/useTakeLocalURLPropsSelectionsEffect';
import {useToggle} from '@gaia/hooks/useToggle';
import {useTracks} from '@gaia/hooks/useTracks';
import {useWaypoints} from '@gaia/hooks/useWaypoints';
import {useUser} from '@gaia/stores/user';
import type {ImportState, SharedData} from '@gaia/types';
import {idle as idleState} from '@gaia/types/ImportState';
import {getLocalStorageItem} from '@gaia/utils';
import {fetchSavedKnownRoutes, getSyncMapboxStyle} from '@gaia/utils/gaia';
import {useInteractionHandlers} from '@gaia/utils/gaia/useInteractionHandlers';
import getBoundsSafely from '@gaia/utils/mapbox/getBoundsSafely';
import Mapbox from '@gaia/utils/mapbox/Mapbox';
import {useSafariPinchZoom} from '@gaia/utils/mapbox/useSafariPinchZoom';
import {useBeforeUnloadConfirmation, useGetSyncPromise} from '@gaia/utils/react';
import {ConfirmImportDialog} from '@map/ConfirmImportDialog';
import {
    ShowKnownRoutesSnackbarContext,
    SidebarInteractionSnackbarContext,
    SidebarInteractionSnackbarState,
} from '@map/contexts';
import {InteractionProvider} from '@map/contexts/InteractionContext';
import {Controls, ControlsDrawer, getMapControlsWidth} from '@map/controls';
import {DnDCTA} from '@map/DnDCTA';
import DraggableWaypoint from '@map/DraggableWaypoint';
import {Legend} from '@map/Legend';
import {HighlightLocation, HoverLineLocation} from '@map/LineLocation';
import {MapHeader} from '@map/MapHeader/MapHeader';
import {FreeTrialModal} from '@map/popups/FreeTrialModal';
import {GoPremiumModal} from '@map/popups/GoPremiumModal';
import {OnboardingPopup} from '@map/popups/UserOnboarding';
import {PrintPreview} from '@map/PrintPreview';
import {SavingSurface} from '@map/SavingSurface';
import {
    AreaDetailsSidebar,
    ClickDisambiguationSidebar,
    CustomSourceSidebar,
    EditAreaSidebar,
    EditFolderSidebar,
    EditLayersSidebar,
    EditTrackSidebar,
    EditWaypointSidebar,
    FallbackSidebar,
    FolderDetailsSidebar,
    getSidebarWidth,
    InteractionSpecSidebar,
    KnownRouteSidebar,
    KnownRoutesSidebar,
    LandSidebar,
    LayerDetailsSidebar,
    LayersSidebar,
    MarkedLocation,
    OverlaysSidebar,
    PreferencesSidebar,
    PrintSidebar,
    SavedItemsSidebar,
    ShareFolderSidebar,
    TrackDetailsSidebar,
    TrailAndPoiSidebar,
    WaypointDetailsSidebar,
    WeatherDetailsSidebar,
} from '@map/sidebars';
import breadcrumbNavigationReducer from '@map/sidebars/FolderDetailsSidebar/breadcrumbNavigationReducer';
import {
    InteractionInstructionsSnackbar,
    KnownRoutesSnackbar,
    RouteEditingSnackbar,
    SidebarInteractionSnackbar,
    UndoSnackbar,
} from '@map/snackbars';
import TrialStatusSnackbar from '@map/TrialStatusSnackbar';
import {stringifyUrlProps} from '@map/urlProps';
import clsx from 'clsx';
import throttle from 'lodash/throttle';
import mapboxgl, {
    EventData,
    LngLat,
    MapboxEvent,
    MapboxOptions,
    MapEventType,
    MapLayerMouseEvent,
    MapTouchEvent,
} from 'mapbox-gl';
import {
    CSSProperties,
    Profiler,
    ProfilerOnRenderCallback,
    Reducer,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';

import {ImportSurface} from './ImportSurface';
import classes from './Map.module.css';
import {sendToggleMapOverlayEvent} from './mapAnalyticsEvents';
import {MapAnnouncement} from './MapAnnouncement';
import {mapboxErrorHandler} from './mapboxErrorHandler';
import {MapFooter} from './MapFooter';
import {onStyleImageMissing} from './onStyleImageMissing';
import {useControlPointPopup} from './useControlPointPopup';
import {useMapInteractionEvents, useMapStableEvents} from './useMapEvents';
import {useOnMapboxStyleLoaded} from './useOnMapboxStyleLoaded';
import {usePreloadMapboxImages} from './usePreloadMapboxImages';
import {useUpdateMapboxStyle} from './useUpdateMapboxStyle';
import {useUrlProps} from './useUrlProps';

mapboxgl.accessToken =
    'pk.eyJ1IjoidHJhaWxiZWhpbmQiLCJhIjoiY2s2aTVkNjZjMzFzZDNncWp1MWNjOGt1eCJ9.F9GkmmsgFzuVkLSJtqa3cg';

export interface MapProps {
    grid?: Grid;
    isEmbedded?: boolean;
    layers?: Layer[];
    legacyPrint?: boolean;
    mapboxOptions?: Partial<MapboxOptions>;
    preview?: boolean;
    savePosition?: boolean;
    sharedData?: SharedData;
    show3DControl?: boolean;
    showArchived?: boolean;
    showAreas?: boolean;
    showControlsDrawer?: boolean;
    showDrawControl?: boolean;
    showFullscreenControl?: boolean;
    showGeolocateControl?: boolean;
    showKnownRoutes?: boolean;
    showOverlaysControl?: boolean;
    showPreferencesControl?: boolean;
    showPrintControl?: boolean;
    showRoutes?: boolean;
    showSavedItemsControl?: boolean;
    showSearchBoxControl?: boolean;
    showTracks?: boolean;
    showWaypoints?: boolean;
    style?: CSSProperties;

    onClick?: (event: MapLayerMouseEvent) => void;
    onMapboxRefChanged?: (map: MapboxMap) => void;
    onMapboxStyleLoaded?: (map: MapboxMap) => void;
    onMouseDown?: (event: MapLayerMouseEvent) => void;
    onMouseMove?: (event: MapLayerMouseEvent) => void;
    onMouseUp?: (event: MapLayerMouseEvent) => void;
    onMoveEnd?: (event: MapboxEvent) => void;
    onResize?: (event: MapEventType) => void;
    onStyleImageMissing?: (event: EventData) => void;
    onTouchEnd?: (event: MapTouchEvent) => void;
    onTouchMove?: (event: MapTouchEvent) => void;
    onTouchStart?: (event: MapTouchEvent) => void;
}

// Safari errors if window.history.replaceState is called more than 100 times in 30 seconds.
export const throttledHistoryReplaceState = throttle(
    (nextSearch: string) => {
        history.replaceState(
            null,
            '',
            location.origin + location.pathname + location.hash + nextSearch
        );
    },
    30000 / 100 + 1,
    {leading: true, trailing: true}
);

const spaceNeedle = new LngLat(-122.349358, 47.620422);
const SIDEBAR_WIDTH = getSidebarWidth();

export function Map(props: MapProps) {
    const getSyncPromise = useGetSyncPromise();

    const user = useUser();
    const {unitSystem} = user;

    const launchDarklyFlags = useLaunchDarkly(user);

    const [controlsExpanded, , handleExpandControls, handleCollapseControls] = useToggle();

    const urlProps = useUrlProps();

    const [interaction, onInteractionChange] = useState<InteractionState>({
        type: 'normal',
        subtype: 'normal',
    });

    const [overlays, onOverlaysChange] = useOverlaysState({
        showTracksProp: props.showTracks,
        showRoutesProp: props.showRoutes,
        showWaypointsProp: props.showWaypoints,
        showAreasProp: props.showAreas,
        showKnownRoutesProp: props.showKnownRoutes,
    });

    const [sharedData, onSharedDataChange] = useState<SharedData>(
        props.sharedData || urlProps.sharedData
    );

    type MapAndBoundMethods = {
        map?: MapboxMap;
        getStyle?: InstanceType<typeof mapboxgl.Map>['getStyle'];
        project?: InstanceType<typeof mapboxgl.Map>['project'];
        querySourceFeatures?: InstanceType<typeof mapboxgl.Map>['querySourceFeatures'];
    };

    const [{map, getStyle, project, querySourceFeatures}, onMapboxRefChanged] = useReducer<
        Reducer<MapAndBoundMethods, MapboxMap>
    >(
        (_: MapAndBoundMethods, m: MapboxMap) => ({
            map: m,
            getStyle: m.getStyle.bind(m),
            project: m.project.bind(m),
            querySourceFeatures: m.querySourceFeatures.bind(m),
        }),
        {}
    );

    useSafariPinchZoom(map);

    useEffect(() => {
        const onMapboxRefChangedProp = props.onMapboxRefChanged;
        if (map) onMapboxRefChangedProp?.(map);
    }, [map, props.onMapboxRefChanged]);

    const [selections, setSelections] = useState<SelectionState[]>(urlProps.selections || []);
    const activeSelection = selections.at(0);

    // import sidebar is set on urlProps when `&sidebar=import` query param is passed
    const [sidebar, onSidebarChange] = useState<SidebarState>(urlProps.sidebar ?? {type: 'hidden'});

    const sidebarOpen = sidebar.type !== 'hidden';

    const [is3D, setIs3D] = useState(false);

    const [showKnownRoutesSnackbar, setShowKnownRoutesSnackbar] = useState(false);
    const [knownRoutes, onKnownRoutesChange] = useState<Record<number, KnownRouteTag>>({});

    // Load a user's known routes if the known route overlay is on
    useEffect(() => {
        if (!overlays.showKnownRoutes) return;
        fetchSavedKnownRoutes(user.isAuthenticated)
            .then((saved) =>
                onKnownRoutesChange((kr) => ({
                    ...kr,
                    ...saved,
                }))
            )
            .catch(console.error);
    }, [overlays.showKnownRoutes, user.isAuthenticated]);

    const [undo, setUndo] = useState<UndoState | undefined>(undefined);

    const [sidebarInteractionSnackbar, setSidebarInteractionSnackbar] =
        useState<SidebarInteractionSnackbarState>({
            message: undefined,
            duration: 3500,
        });

    const closeSidebarInteractionSnackbar = useCallback(
        () => setSidebarInteractionSnackbar({message: undefined}),
        [setSidebarInteractionSnackbar]
    );

    const importRef = useRef<HTMLInputElement>(null);

    const [importState, setImportState] = useState<ImportState>(() => {
        if (
            urlProps.sidebar?.type === 'savedItems' &&
            urlProps.importState?.status === 'sidebarOpen'
        ) {
            return {status: 'sidebarOpen'};
        } else {
            return idleState;
        }
    });

    const [tracks, trackDispatcher, unsavedTracks] = useTracks(
        project,
        sharedData,
        interaction.type,
        importState
    );
    const [areas, areaDispatcher, unsavedAreas] = useAreas(
        sharedData,
        interaction.type,
        importState
    );
    const [waypoints, waypointDispatcher, unsavedWaypoints] = useWaypoints(
        tracks,
        trackDispatcher,
        interaction.type,
        sharedData,
        importState
    );
    const [folders, folderDispatcher] = useFolders({
        areas,
        waypoints,
        tracks,
        sharedData,
        interactionType: interaction.type,
        importState,
        areaDispatcher,
        trackDispatcher,
        waypointDispatcher,
    });

    const [breadcrumbNavigationState, dispatchBreadcrumbNavigation] = useReducer(
        breadcrumbNavigationReducer,
        []
    );

    // Update the folder breadcrumb navigation when the sidebar changes
    useEffect(() => {
        if (sidebar.type === 'folderDetails') {
            dispatchBreadcrumbNavigation({type: 'push', state: sidebar});
            return;
        }

        // Ensure the navigation persists when clicking the back button
        // after navigating to an item from its parent folder
        if (
            (sidebar.type === 'trackDetails' ||
                sidebar.type === 'waypointDetails' ||
                sidebar.type === 'areaDetails' ||
                sidebar.type === 'editFolder') &&
            sidebar.prevSidebar?.type === 'folderDetails'
        ) {
            return;
        }

        dispatchBreadcrumbNavigation({type: 'reset'});
    }, [sidebar]);

    // Visually highlight the active folder and its contents on the map
    const [activeFolderIds] = useHighlightActiveFolder({sidebar, folders});

    // Process imports when they change
    useReadFileEffect(importState, setImportState);

    // Dispatch the import data when the user confirms it or it is a small import
    useDispatchImportEffect({
        importState,
        setImportState,
        waypointDispatcher,
        trackDispatcher,
        areaDispatcher,
        overlays,
        onOverlaysChange,
        setSelections,
    });

    // Update the map bounds once an import has been parsed
    useFitMapToImportEffect(map, importState);

    // Update the map bounds when the selection changes
    useFitMapToSelectionEffect({
        interaction,
        map,
        waypoints,
        tracks,
        areas,
        knownRoutes,
        project,
        selection: activeSelection,
        sidebarWidth: sidebarOpen ? SIDEBAR_WIDTH : 0,
        controlsExpanded,
    });

    // Update the map bounds when navigating into a folder
    useFitMapToFolderEffect({
        interaction,
        map,
        activeFolderIds,
        folders,
        sidebarWidth: sidebarOpen ? SIDEBAR_WIDTH : 0,
        controlsExpanded,
    });

    // Trigger analytics event on import
    useImportAnalyticsEffect(importState);

    const [{layers, userLayers, layerTypes, layerTypeCategories}, userLayerDispatcher] = useLayers(
        props.layers,
        urlProps.layers
    );

    // Modify map control behavior when switching between 2 and 3D
    use3D({map, is3D});

    const [visibilities, setVisibilities] = usePersistentState<Record<string, boolean>>(
        'visibilities',
        {}
    );

    // When a saved item is created, update its visibility to match that of its parent folder
    const [setVisibilityQueue] = useParentFolderVisibility({
        areas,
        tracks,
        folders,
        waypoints,
        setVisibilities,
    });

    const [grid, onGridChange] = usePersistentState<Grid>('grid', props.grid ?? 'none', {
        onChange: sendToggleMapOverlayEvent,
    });

    const [{mapboxStyle, transformRequest}, , isMapboxStyleLoaded] = getSyncMapboxStyle({
        areas,
        bounds: getBoundsSafely(map),
        getSyncPromise,
        grid: props.layers ? 'none' : grid,
        sharedData,
        getStyle,
        knownRoutes,
        interaction,
        is3D,
        isPrinting: sidebar.type === 'print',
        layers,
        layerTypes,
        preview: props.preview,
        isEmbedded: props.isEmbedded,
        project,
        querySourceFeatures,
        showAreas: !props.layers && overlays.showAreas,
        showPublicTracks: !props.layers && overlays.showPublicTracks,
        showTracks: !props.layers && overlays.showTracks,
        showRoutes: !props.layers && overlays.showRoutes,
        showWaypointLabels: overlays.showWaypointLabels,
        showWaypoints: !props.layers && overlays.showWaypoints,
        showKnownRoutes: !props.layers && overlays.showKnownRoutes,
        showArchived: props.showArchived || false,
        tracks,
        waypoints,
        zoom: map?.getZoom(),
        selections,
        visibilities,
        activeFolderIds,
        isAuthenticated: user.isAuthenticated,
        unitSystem,
    });

    // Trigger an event when the mapbox style is loaded for real. Currently only used by the
    // <HuntingMapPreview> component.
    useOnMapboxStyleLoaded(map, isMapboxStyleLoaded, props.onMapboxStyleLoaded);

    // Handles import errors.
    useEffect(() => {
        if (importState.status !== 'selecting') return;
        if (!importState.error) return;

        // Open the Saved Items Sidebar to show the import error
        if (sidebar.type !== 'savedItems') onSidebarChange({type: 'savedItems'});
        // Clear the file selection
        if (importRef.current) importRef.current.value = '';
    }, [importState, sidebar.type]);

    // Conditionally shows the 'are you sure?' dialog when the user navigates away.
    const showBeforeUnloadConfirmation =
        interaction.type === 'editingWaypoint' ||
        interaction.type === 'editingRoute' ||
        interaction.type === 'editingArea';
    useBeforeUnloadConfirmation(showBeforeUnloadConfirmation);

    const onProfilerRenderRef = useRef<ProfilerOnRenderCallback>(() => {});

    // Computes if the Mapbox style has changed and updates the map as necessary.
    useUpdateMapboxStyle(map, mapboxStyle);

    useTakeLocalURLPropsSelectionsEffect({
        areaDispatcher,
        waypointDispatcher,
        trackDispatcher,
        selections: urlProps.selections,
    });

    // Embedded maps provide a FeatureCollection via sharedData
    // Embedded maps need that geometry in bounds
    const {sharedData: sharedDataProp} = props;

    useEffect(() => {
        if (!map || sharedDataProp?.type !== 'FeatureCollection') return;
        onSharedDataChange(sharedDataProp);
    }, [sharedDataProp, map]);

    const [initialCenter] = useState(() => {
        if (props.savePosition === undefined) return undefined;
        if (urlProps.center) return urlProps.center;
        const fallback = user.geolocation !== null ? LngLat.convert(user.geolocation) : spaceNeedle;
        return getLocalStorageItem('center', fallback);
    });

    const [initialZoom] = useState(() => {
        if (props.savePosition === undefined) return undefined;
        if (urlProps.zoom) return urlProps.zoom;
        return getLocalStorageItem('zoom', 11);
    });

    // The following are called by preserveMapState, which is triggered on map "moveend" to save the map state
    // to the URL and localStorage. Storing center and zoom in state ensures that these updates will also
    // trigger a render, and keep mapbox styles that don't rely on user interactions in sync with the view.
    const [, setCenter] = usePersistentState('center', initialCenter);
    const [, setZoom] = usePersistentState('zoom', initialZoom);

    const onPreservableMapStateChanged = useEvent(() => {
        if (!layers || !layerTypes) return;
        const currentCenter = map?.getCenter().wrap();
        const currentZoom = map?.getZoom();

        const nextSearch = stringifyUrlProps({
            ...(props.savePosition && currentCenter && {center: currentCenter, zoom: currentZoom}),
            ...(!props.layers && {
                layers: layers
                    .filter(
                        // Custom layer previews are unsaved and should not update the URL
                        (layer) => layer.type !== 'preview'
                    )
                    .map(
                        (layer) =>
                            // Custom layers do not have a displayKey, so they will use id
                            layerTypes[layer.type].displayKey || layerTypes[layer.type].id
                    ),
            }),
            ...(!props.isEmbedded && {sharedData}),
            sidebar,
            selections,
        });

        if (nextSearch !== location.search) {
            throttledHistoryReplaceState(nextSearch);
        }

        if (props.savePosition && currentCenter) {
            setCenter(currentCenter);
            setZoom(currentZoom);
        }
    });

    // Preserve map state when layers, sidebar, selections, or sharedData change
    useEffect(
        () => onPreservableMapStateChanged(),
        [layers, sidebar, selections, sharedData, onPreservableMapStateChanged]
    );

    const {
        cursor: {current: cursor},
        interactionHandlers,
    } = useInteractionHandlers({
        interaction,
        mapboxStyle: mapboxStyle,
        onClickExternal: props.onClick,
        onInteractionChange,
        onMouseDownExternal: props.onMouseDown,
        onMouseMoveExternal: props.onMouseMove,
        onMouseUpExternal: props.onMouseUp,
        setSelections,
        selections,
        onTouchEndExternal: props.onTouchEnd,
        onTouchMoveExternal: props.onTouchMove,
        onTouchStartExternal: props.onTouchStart,
        trackDispatcher,
        tracks,
        waypointDispatcher,
        waypoints,
        areaDispatcher,
        areas,
        sharedData,
        map,
        onOverlaysChange,
        overlays,
        project,
        sidebar,
        onSidebarChange,
        bounds: getBoundsSafely(map),
        unitSystem,
        isAuthenticated: user.isAuthenticated,
        importState,
        setImportState,
        querySourceFeatures,
        isEmbedded: props.isEmbedded,
        controlsExpanded,
    });

    const onMapIdle = useCallback(() => {
        /* for future use, probably a `useEvent` instead */
    }, []);

    const stableEventHandlers = useMemo(
        () => ({
            onIdle: onMapIdle,
            onMoveEnd: onPreservableMapStateChanged,
            onStyleImageMissing,
            onError: mapboxErrorHandler,
        }),
        [onMapIdle, onPreservableMapStateChanged]
    );

    useMapStableEvents(map, stableEventHandlers);

    useMapInteractionEvents(map, interactionHandlers);

    useEffect(() => {
        if (map) map.getCanvas().style.cursor = cursor;
    }, [cursor, map]);

    // Triggers undo snackbar and updates folder membership when import has saved
    useImportHasSaved({
        importState,
        setImportState,
        setUndo,
        waypointDispatcher,
        trackDispatcher,
        areaDispatcher,
        folderDispatcher,
        selections,
        setSelections,
        tracks,
        areas,
        waypoints,
        onSidebarChange,
    });

    // Open the sidebar that corresponds to the current interaction state if no sidebar is shown
    useEffect(() => {
        if (sidebar.type === 'hidden' && interaction.type === 'editingRoute') {
            onSidebarChange({type: 'editTrack'});
        } else if (sidebar.type === 'hidden' && interaction.type === 'editingWaypoint') {
            onSidebarChange({type: 'editWaypoint'});
        } else if (sidebar.type === 'hidden' && interaction.type === 'editingArea') {
            onSidebarChange({type: 'editArea'});
        } else if (
            selections.length > 1 &&
            interaction.type === 'normal' &&
            sidebar.type !== 'folderDetails' &&
            sidebar.type !== 'savedItems'
        ) {
            // Show the saved items list when cmd+clicking to select multiple items on the map
            onSidebarChange({type: 'savedItems'});
        }
    }, [sidebar.type, interaction.type, selections]);

    // This would otherwise be a pointless useMemo, but it's passed to <ControlsDrawer> which is
    // React.memo'd. If this isn't stabilized, that memoization is useless.
    const showControls = useMemo(
        () => ({
            draw: !!props.showDrawControl,
            layer: !props.layers, // sic
            savedItems: props.showSavedItemsControl !== false, // `true` if undefined
            overlays: props.showOverlaysControl !== false, // "
            preferences: props.showPreferencesControl !== false, // "
            print: props.showPrintControl !== false, // "
        }),
        [
            props.showDrawControl,
            props.showSavedItemsControl,
            props.showPreferencesControl,
            props.showPrintControl,
            props.layers,
            props.showOverlaysControl,
        ]
    );

    const {showControlsDrawer = true} = props;

    const controlsDrawerWidth = getMapControlsWidth(controlsExpanded);
    const visibleMapControlsWidth = controlsDrawerWidth + (sidebarOpen ? SIDEBAR_WIDTH : 0);

    // Preloads a small pre-determined set of images and adds them to the map.
    usePreloadMapboxImages(map, mapboxStyle.sprite);

    // The map is remounted when toggling the print preview. If a map already exists
    // use the center and zoom instead of falling back to the initial state
    const center = map?.getCenter().wrap() ?? (props.savePosition ? initialCenter : undefined);
    const zoom = map?.getZoom() ?? (props.savePosition ? initialZoom : undefined);

    const mapSurfaceRef = useRef<HTMLDivElement>(null);

    const uiClasses = clsx(classes.ui, {controlsExpanded, sidebarOpen});

    const showDnDCTA = interaction.type === 'normal' && interaction.subtype === 'draggingFile';
    const showSavingSurface = importState.status === 'saving';
    const showConfirmImportDialog = importState.status === 'loading' && importState.pendingImport;

    // Returns a popup & container if there is an active area or route control point selection
    const [popupComponent, popupContainer] = useControlPointPopup({
        map,
        popupProps: {
            interaction,
            onInteractionChange,
            selection: activeSelection,
            setSelections,
            overlays,
            onOverlaysChange,
        },
    });

    const mapbox = (
        <Mapbox
            mapboxStyle={mapboxStyle}
            options={props.mapboxOptions}
            // The map is remounted when toggling the print preview. If a map already exists
            // use the center and zoom instead of falling back to the initial state
            center={center}
            zoom={zoom}
            onMapboxRefChanged={onMapboxRefChanged}
            popupComponent={popupComponent}
            popupContainer={popupContainer}
            transformRequest={transformRequest}
            sidebarType={sidebar.type}
        />
    );

    const gaiaMap = props.isEmbedded ? (
        mapbox
    ) : (
        <ImportSurface
            interaction={interaction}
            onInteractionChange={onInteractionChange}
            onSidebarChange={onSidebarChange}
            setImportState={setImportState}
        >
            {mapbox}
        </ImportSurface>
    );

    return (
        <Profiler id='Map' onRender={onProfilerRenderRef.current}>
            <main className={uiClasses} style={props.style}>
                <div ref={mapSurfaceRef} className={classes.mapboxWrapper}>
                    {sidebar.type !== 'print' ? (
                        gaiaMap
                    ) : (
                        <Suspense fallback={null}>
                            <PrintPreview
                                mapControlsOffset={getMapControlsWidth(controlsExpanded)}
                                layers={userLayers
                                    ?.filter(({isVisible}) => isVisible)
                                    ?.map(({layer}) => layer)}
                                layerTypes={layerTypes}
                                map={map}
                            >
                                {gaiaMap}
                            </PrintPreview>
                        </Suspense>
                    )}

                    <i className={classes.displayContents} key='HighlightLocation'>
                        <HighlightLocation project={project} />
                    </i>

                    <i className={classes.displayContents} key='HoverLineLocation'>
                        <HoverLineLocation
                            project={project}
                            interaction={interaction}
                            getSyncPromise={getSyncPromise}
                        />
                    </i>

                    <DraggableWaypoint
                        map={map}
                        interaction={interaction}
                        onInteractionChange={onInteractionChange}
                    />

                    {launchDarklyFlags.allowFreeTrials && (
                        <>
                            <TrialStatusSnackbar
                                mapControlsOffset={visibleMapControlsWidth}
                                open={
                                    !interaction.type.includes('editing') &&
                                    sidebar.type !== 'print' &&
                                    !props.isEmbedded
                                }
                                shouldShowErrorIfExpired={!!urlProps.showFreeTrialOffer}
                            />
                            <FreeTrialModal shouldShowIfEligible={!!urlProps.showFreeTrialOffer} />
                        </>
                    )}

                    <KnownRoutesSnackbar
                        mapControlsOffset={visibleMapControlsWidth}
                        show={showKnownRoutesSnackbar}
                    />

                    <UndoSnackbar
                        undo={undo}
                        setUndo={setUndo}
                        interactionType={interaction.type}
                        mapControlsOffset={controlsDrawerWidth}
                    />

                    {interaction.type !== 'editingRoute' && (
                        <InteractionInstructionsSnackbar
                            instructions={
                                interaction.type !== 'normal' &&
                                interaction.type !== 'editingFolder'
                                    ? interaction?.instructions
                                    : undefined
                            }
                            mapControlsOffset={visibleMapControlsWidth}
                        />
                    )}

                    {interaction.type === 'editingRoute' && interaction.track.isGeometryLoaded && (
                        <RouteEditingSnackbar
                            interaction={interaction}
                            onInteractionChange={onInteractionChange}
                            project={project}
                            mapControlsOffset={visibleMapControlsWidth}
                            mapSurfaceContainerRef={mapSurfaceRef}
                        />
                    )}

                    <SidebarInteractionSnackbar
                        autoHideDuration={sidebarInteractionSnackbar.duration}
                        open={!!sidebarInteractionSnackbar.message}
                        mapControlsOffset={controlsDrawerWidth}
                        message={sidebarInteractionSnackbar.message}
                        onClose={closeSidebarInteractionSnackbar}
                    />
                </div>

                {!props.isEmbedded && <MapHeader />}

                {sidebar.type !== 'print' && (
                    <Controls
                        is3D={is3D}
                        map={map}
                        onKnownRoutesChange={onKnownRoutesChange}
                        onSidebarChange={onSidebarChange}
                        interactionType={interaction.type}
                        onInteractionChange={onInteractionChange}
                        setIs3D={setIs3D}
                        setSelections={setSelections}
                        sharedData={sharedData}
                        showFullscreenControl={!!props.showFullscreenControl}
                        showGeolocateControl={props.showGeolocateControl !== false}
                        showSearchBoxControl={!!props.showSearchBoxControl}
                        show3DControl={!!props.show3DControl}
                        userLayers={userLayers}
                    />
                )}

                {!props.isEmbedded && <MapFooter />}

                <SidebarInteractionSnackbarContext.Provider
                    value={[sidebarInteractionSnackbar, setSidebarInteractionSnackbar]}
                >
                    <ShowKnownRoutesSnackbarContext.Provider value={setShowKnownRoutesSnackbar}>
                        {showControlsDrawer && (
                            <div data-pw='sidebar-container' className={classes.sidebar}>
                                <Suspense fallback={<FallbackSidebar />}>
                                    {sidebar.type === 'layers' && (
                                        <LayersSidebar
                                            onSidebarChange={onSidebarChange}
                                            userLayers={userLayers}
                                            userLayerDispatcher={userLayerDispatcher}
                                            layerTypes={layerTypes}
                                        />
                                    )}

                                    {sidebar.type === 'editLayers' && (
                                        <EditLayersSidebar
                                            onSidebarChange={onSidebarChange}
                                            sidebar={sidebar}
                                            userLayers={userLayers}
                                            userLayerDispatcher={userLayerDispatcher}
                                            layerTypes={layerTypes}
                                            layerTypeCategories={layerTypeCategories}
                                            center={map?.getCenter().wrap()}
                                        />
                                    )}

                                    {sidebar.type === 'print' && (
                                        <PrintSidebar
                                            map={map}
                                            onSidebarChange={onSidebarChange}
                                            layers={layers}
                                            layerTypes={layerTypes}
                                            setImportState={setImportState}
                                            userLayers={userLayers}
                                            sharedData={sharedData}
                                        />
                                    )}

                                    {sidebar.type === 'layerDetails' && (
                                        <LayerDetailsSidebar
                                            sidebar={sidebar}
                                            userLayers={userLayers}
                                            userLayerDispatcher={userLayerDispatcher}
                                            onSidebarChange={onSidebarChange}
                                        />
                                    )}

                                    {sidebar.type === 'overlays' && (
                                        <OverlaysSidebar
                                            onSidebarChange={onSidebarChange}
                                            onOverlaysChange={onOverlaysChange}
                                            overlays={overlays}
                                            disableShowWaypoints={props.showWaypoints !== undefined}
                                            disableShowTracks={props.showTracks !== undefined}
                                            disableShowAreas={props.showAreas !== undefined}
                                            disableShowRoutes={props.showRoutes !== undefined}
                                            disableShowKnownRoutes={
                                                props.showKnownRoutes !== undefined
                                            }
                                            onGridChange={onGridChange}
                                            grid={grid}
                                            sidebar={sidebar}
                                            is3D={is3D}
                                        />
                                    )}

                                    {sidebar.type === 'customSource' && (
                                        <CustomSourceSidebar
                                            onSidebarChange={onSidebarChange}
                                            userLayerDispatcher={userLayerDispatcher}
                                        />
                                    )}

                                    {sidebar.type === 'preferences' && (
                                        <PreferencesSidebar onSidebarChange={onSidebarChange} />
                                    )}

                                    {sidebar.type === 'savedItems' && (
                                        <SavedItemsSidebar
                                            onSidebarChange={onSidebarChange}
                                            sidebar={sidebar}
                                            setSelections={setSelections}
                                            selections={selections}
                                            trackDispatcher={trackDispatcher}
                                            waypointDispatcher={waypointDispatcher}
                                            areaDispatcher={areaDispatcher}
                                            folderDispatcher={folderDispatcher}
                                            waypoints={waypoints}
                                            tracks={tracks}
                                            areas={areas}
                                            folders={folders}
                                            visibilities={visibilities}
                                            setVisibilities={setVisibilities}
                                            importState={importState}
                                            importRef={importRef}
                                            setImportState={setImportState}
                                            setUndo={setUndo}
                                            map={map}
                                        />
                                    )}

                                    {activeSelection?.type === 'osm' &&
                                        activeSelection.properties?.type === 'land' &&
                                        sidebar.type === 'selection' && (
                                            <LandSidebar
                                                selection={activeSelection}
                                                setSelections={setSelections}
                                                knownRoutes={knownRoutes}
                                                onKnownRoutesChange={onKnownRoutesChange}
                                                onSidebarChange={onSidebarChange}
                                                map={map}
                                                sidebar={sidebar}
                                                markedLocation={
                                                    <MarkedLocation
                                                        interaction={interaction}
                                                        onInteractionChange={onInteractionChange}
                                                        onSidebarChange={onSidebarChange}
                                                        sidebar={sidebar}
                                                    />
                                                }
                                            />
                                        )}

                                    {activeSelection?.type === 'osm' &&
                                        activeSelection.properties?.type !== 'land' &&
                                        sidebar.type === 'selection' && (
                                            <TrailAndPoiSidebar
                                                selection={activeSelection}
                                                setSelections={setSelections}
                                                knownRoutes={knownRoutes}
                                                onKnownRoutesChange={onKnownRoutesChange}
                                                onSidebarChange={onSidebarChange}
                                                map={map}
                                                sidebar={sidebar}
                                                markedLocation={
                                                    <MarkedLocation
                                                        interaction={interaction}
                                                        onInteractionChange={onInteractionChange}
                                                        onSidebarChange={onSidebarChange}
                                                        sidebar={sidebar}
                                                    />
                                                }
                                                waypointDispatcher={waypointDispatcher}
                                            />
                                        )}

                                    {activeSelection?.type === 'knownRoute' &&
                                    sidebar.type === 'knownRoute' ? (
                                        <InteractionProvider value={interaction}>
                                            <KnownRouteSidebar
                                                selection={activeSelection}
                                                setSelections={setSelections}
                                                knownRoutes={knownRoutes}
                                                onKnownRoutesChange={onKnownRoutesChange}
                                                onSidebarChange={onSidebarChange}
                                                sidebar={sidebar}
                                                map={map}
                                                markedLocation={
                                                    <MarkedLocation
                                                        interaction={interaction}
                                                        onInteractionChange={onInteractionChange}
                                                        onSidebarChange={onSidebarChange}
                                                        sidebar={sidebar}
                                                    />
                                                }
                                            />
                                        </InteractionProvider>
                                    ) : null}

                                    {sidebar.type === 'knownRoutes' &&
                                    activeSelection?.type === 'osm' ? (
                                        <KnownRoutesSidebar
                                            onSidebarChange={onSidebarChange}
                                            setSelections={setSelections}
                                            sidebar={sidebar}
                                            selection={activeSelection}
                                        />
                                    ) : null}

                                    {activeSelection?.type === 'interactionSpec' &&
                                    sidebar.type === 'interactionSpec' ? (
                                        <InteractionSpecSidebar
                                            selection={activeSelection}
                                            setSelections={setSelections}
                                            mapboxStyle={mapboxStyle}
                                            onSidebarChange={onSidebarChange}
                                            sidebar={sidebar}
                                            markedLocation={
                                                <MarkedLocation
                                                    interaction={interaction}
                                                    onInteractionChange={onInteractionChange}
                                                    onSidebarChange={onSidebarChange}
                                                    sidebar={sidebar}
                                                />
                                            }
                                        />
                                    ) : null}

                                    {sidebar.type === 'folderDetails' ? (
                                        <FolderDetailsSidebar
                                            controlsExpanded={controlsExpanded}
                                            dispatchBreadcrumbNavigation={
                                                dispatchBreadcrumbNavigation
                                            }
                                            breadcrumbState={breadcrumbNavigationState}
                                            onSidebarChange={onSidebarChange}
                                            sidebar={sidebar}
                                            setSelections={setSelections}
                                            selections={selections}
                                            trackDispatcher={trackDispatcher}
                                            waypointDispatcher={waypointDispatcher}
                                            areaDispatcher={areaDispatcher}
                                            folderDispatcher={folderDispatcher}
                                            waypoints={waypoints}
                                            tracks={tracks}
                                            areas={areas}
                                            folders={folders}
                                            visibilities={visibilities}
                                            setVisibilities={setVisibilities}
                                            setUndo={setUndo}
                                            map={map}
                                            sharedData={sharedData}
                                            onInteractionChange={onInteractionChange}
                                        />
                                    ) : null}

                                    {sidebar.type === 'waypointDetails' && (
                                        <WaypointDetailsSidebar
                                            onSidebarChange={onSidebarChange}
                                            onInteractionChange={onInteractionChange}
                                            sidebar={sidebar}
                                            waypoints={waypoints}
                                            waypointDispatcher={waypointDispatcher}
                                            trackDispatcher={trackDispatcher}
                                            areaDispatcher={areaDispatcher}
                                            setSelections={setSelections}
                                            tracks={tracks}
                                            map={map}
                                            visibilities={visibilities}
                                            setVisibilities={setVisibilities}
                                            sharedData={sharedData}
                                            folders={folders}
                                            folderDispatcher={folderDispatcher}
                                        />
                                    )}

                                    {sidebar.type === 'editWaypoint' && (
                                        <EditWaypointSidebar
                                            onSidebarChange={onSidebarChange}
                                            onInteractionChange={onInteractionChange}
                                            interaction={interaction}
                                            sidebar={sidebar}
                                            waypointDispatcher={waypointDispatcher}
                                            tracks={tracks}
                                            setVisibilities={setVisibilities}
                                            setVisibilityQueue={setVisibilityQueue}
                                            visibilities={visibilities}
                                        />
                                    )}

                                    {sidebar.type === 'trackDetails' && (
                                        <InteractionProvider value={interaction}>
                                            <TrackDetailsSidebar
                                                onSidebarChange={onSidebarChange}
                                                onInteractionChange={onInteractionChange}
                                                sidebar={sidebar}
                                                setSelections={setSelections}
                                                tracks={tracks}
                                                areaDispatcher={areaDispatcher}
                                                trackDispatcher={trackDispatcher}
                                                waypointDispatcher={waypointDispatcher}
                                                map={map}
                                                visibilities={visibilities}
                                                setVisibilities={setVisibilities}
                                                sharedData={sharedData}
                                                project={project}
                                                interaction={interaction}
                                                markedLocation={
                                                    <MarkedLocation
                                                        interaction={interaction}
                                                        onInteractionChange={onInteractionChange}
                                                        onSidebarChange={onSidebarChange}
                                                        sidebar={sidebar}
                                                    />
                                                }
                                                folders={folders}
                                                folderDispatcher={folderDispatcher}
                                            />
                                        </InteractionProvider>
                                    )}

                                    {sidebar.type === 'editTrack' && (
                                        <InteractionProvider value={interaction}>
                                            <EditTrackSidebar
                                                onSidebarChange={onSidebarChange}
                                                onInteractionChange={onInteractionChange}
                                                interaction={interaction}
                                                sidebar={sidebar}
                                                trackDispatcher={trackDispatcher}
                                                setVisibilities={setVisibilities}
                                                setVisibilityQueue={setVisibilityQueue}
                                                visibilities={visibilities}
                                                project={project}
                                            />
                                        </InteractionProvider>
                                    )}

                                    {sidebar.type === 'areaDetails' && (
                                        <AreaDetailsSidebar
                                            onSidebarChange={onSidebarChange}
                                            onInteractionChange={onInteractionChange}
                                            sidebar={sidebar}
                                            setSelections={setSelections}
                                            areas={areas}
                                            areaDispatcher={areaDispatcher}
                                            trackDispatcher={trackDispatcher}
                                            waypointDispatcher={waypointDispatcher}
                                            map={map}
                                            setVisibilities={setVisibilities}
                                            visibilities={visibilities}
                                            sharedData={sharedData}
                                            markedLocation={
                                                <MarkedLocation
                                                    interaction={interaction}
                                                    onInteractionChange={onInteractionChange}
                                                    onSidebarChange={onSidebarChange}
                                                    sidebar={sidebar}
                                                />
                                            }
                                            folders={folders}
                                            folderDispatcher={folderDispatcher}
                                        />
                                    )}

                                    {sidebar.type === 'editArea' && (
                                        <EditAreaSidebar
                                            onSidebarChange={onSidebarChange}
                                            sidebar={sidebar}
                                            areaDispatcher={areaDispatcher}
                                            interaction={interaction}
                                            onInteractionChange={onInteractionChange}
                                            setSelections={setSelections}
                                            setVisibilities={setVisibilities}
                                            setVisibilityQueue={setVisibilityQueue}
                                            visibilities={visibilities}
                                        />
                                    )}

                                    {sidebar.type === 'editFolder' && (
                                        <EditFolderSidebar
                                            onSidebarChange={onSidebarChange}
                                            sidebar={sidebar}
                                            folderDispatcher={folderDispatcher}
                                            interaction={interaction}
                                            setVisibilities={setVisibilities}
                                            setVisibilityQueue={setVisibilityQueue}
                                            visibilities={visibilities}
                                            onInteractionChange={onInteractionChange}
                                        />
                                    )}

                                    {sidebar.type === 'disambiguation' && (
                                        <ClickDisambiguationSidebar
                                            onInteractionChange={onInteractionChange}
                                            onSidebarChange={onSidebarChange}
                                            sidebar={sidebar}
                                            setSelections={setSelections}
                                            trackDispatcher={trackDispatcher}
                                            waypointDispatcher={waypointDispatcher}
                                            areaDispatcher={areaDispatcher}
                                            folderDispatcher={folderDispatcher}
                                            zoom={map?.getZoom()}
                                            layerTypes={layerTypes}
                                            onKnownRoutesChange={onKnownRoutesChange}
                                        />
                                    )}

                                    {sidebar.type === 'weather' && (
                                        <WeatherDetailsSidebar
                                            onSidebarChange={onSidebarChange}
                                            sidebar={sidebar}
                                            onInteractionChange={onInteractionChange}
                                        />
                                    )}

                                    {sidebar.type === 'shareFolder' && (
                                        <ShareFolderSidebar
                                            sidebar={sidebar}
                                            onSidebarChange={onSidebarChange}
                                            folderDispatcher={folderDispatcher}
                                        />
                                    )}
                                </Suspense>
                            </div>
                        )}
                    </ShowKnownRoutesSnackbarContext.Provider>
                </SidebarInteractionSnackbarContext.Provider>

                {showControlsDrawer && (
                    <ControlsDrawer
                        onSidebarChange={onSidebarChange}
                        sidebar={sidebar}
                        onExpand={handleExpandControls}
                        onCollapse={handleCollapseControls}
                        map={map}
                        interactionType={interaction.type}
                        importState={importState}
                        setImportState={setImportState}
                        isEmbedded={props.isEmbedded}
                        is3D={is3D}
                        setSelections={setSelections}
                        showControls={showControls}
                    />
                )}

                <OnboardingPopup
                    userLayerDispatcher={userLayerDispatcher}
                    layerTypes={layerTypes}
                    userLayers={userLayers}
                    onSidebarChange={onSidebarChange}
                />

                <GoPremiumModal />

                {!props.isEmbedded && <MapAnnouncement />}

                {/* "surfaces" that are conditionally rendered modals that cover the entire UI */}

                {showSavingSurface && (
                    <SavingSurface
                        unsaved={unsavedWaypoints + unsavedTracks + unsavedAreas}
                        importState={importState}
                        setImportState={setImportState}
                    />
                )}

                {showDnDCTA && <DnDCTA />}

                {showConfirmImportDialog && (
                    <ConfirmImportDialog
                        importState={importState}
                        setImportState={setImportState}
                    />
                )}

                <Legend map={map} layers={layers} layerTypes={layerTypes} />
            </main>
        </Profiler>
    );
}
